import { useCallback, useEffect, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'

import { useAuthStore } from '@/store/authStore'

import { useResourcesApi } from '@/services/api/resourcesApi'
import { MessageLevel, ErrorHandlingStrategy } from '@/types/api'
import { AcademicAdmission } from '@/types/forms/academicAdmission'

interface PreloadData {
    program: string
    admissionType: string
    campus: string
    studyModality: string
    startPeriod: string
    educationModality: string
}

interface UseInscriptionPreloadProps {
    setValue?: UseFormSetValue<AcademicAdmission>
    onDataLoaded?: (data: PreloadData) => void
}

interface UseInscriptionPreloadReturn {
    preloadData: PreloadData | null
    isLoading: boolean
    error: string | null
    clearPreloadData: () => void
    hasPreloadData: boolean
}

// Helper function to capture URL parameters
const captureUrlParamsHelper = (clearPreloadData: () => void) => {
    try {
        console.log('🔍 [PRELOAD] URL actual:', window.location.href)
        const params = new URLSearchParams(window.location.search)
        const codPrograma = params.get('CodPrograma')
        const modalidad = params.get('modalidad')

        console.log('🔍 [PRELOAD] Parámetros encontrados:', {
            codPrograma,
            modalidad
        })

        const existingCodPrograma = sessionStorage.getItem(
            'preload_codPrograma'
        )
        const existingModalidad = sessionStorage.getItem('preload_modalidad')

        if (codPrograma && modalidad) {
            if (
                (existingCodPrograma && existingCodPrograma !== codPrograma) ||
                (existingModalidad && existingModalidad !== modalidad)
            ) {
                console.log(
                    '🧹 [PRELOAD] Nuevos parámetros detectados, limpiando anteriores'
                )
                clearPreloadData()
            }

            sessionStorage.setItem('preload_codPrograma', codPrograma)
            sessionStorage.setItem('preload_modalidad', modalidad)
            console.log('✅ [PRELOAD] Parámetros guardados:', {
                codPrograma,
                modalidad
            })

            return { codPrograma, modalidad }
        } else if (existingCodPrograma || existingModalidad) {
            console.log(
                '⚠️ [PRELOAD] Manteniendo datos almacenados por navegación interna'
            )
        } else {
            console.log('❌ [PRELOAD] No se encontraron parámetros de precarga')
        }
    } catch (error) {
        console.error('❌ [PRELOAD] Error capturando parámetros:', error)
    }

    return null
}

// Helper to transform API data
const transformApiData = (apiData: any): PreloadData => ({
    program: apiData.program || '',
    admissionType: apiData.admissionType || '',
    campus: apiData.campus || '',
    studyModality: apiData.studyModality || '',
    startPeriod: apiData.startPeriod || '',
    educationModality: apiData.educationModality || ''
})

// Helper function to fetch preload data
const fetchPreloadDataHelper = async (
    codPrograma: string,
    modalidad: string,
    resourcesApi: any,
    setIsLoading: (loading: boolean) => void,
    setError: (error: string | null) => void,
    setPreloadData: (data: PreloadData) => void,
    onDataLoaded?: (data: PreloadData) => void
) => {
    try {
        console.log('📡 [PRELOAD] Iniciando llamada a API:', {
            codPrograma,
            modalidad
        })
        setIsLoading(true)
        setError(null)

        const response = await resourcesApi.getInscriptionPreload({
            codPrograma,
            modalidad
        })

        if (response.status === 200 && response.data) {
            const apiData = response.data.data || response.data
            const data = transformApiData(apiData)

            setPreloadData(data)
            onDataLoaded?.(data)
            console.log('✅ [PRELOAD] Datos precargados exitosamente:', data)

            return data
        }

        throw new Error(`Respuesta inválida: status ${response.status}`)
    } catch (err) {
        const errorMessage =
            err instanceof Error ? err.message : 'Error desconocido'

        setError(errorMessage)
        console.error('❌ [PRELOAD] Error obteniendo datos:', err)

        return null
    } finally {
        setIsLoading(false)
    }
}

export function useInscriptionPreload({
    setValue,
    onDataLoaded
}: UseInscriptionPreloadProps = {}): UseInscriptionPreloadReturn {
    const { isAuthenticated } = useAuthStore()
    const [preloadData, setPreloadData] = useState<PreloadData | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasProcessedPreload, setHasProcessedPreload] = useState(false)

    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const clearPreloadData = useCallback(() => {
        sessionStorage.removeItem('preload_codPrograma')
        sessionStorage.removeItem('preload_modalidad')
        setPreloadData(null)
        setError(null)
        setHasProcessedPreload(false)
    }, [])

    const captureUrlParams = useCallback(() => {
        return captureUrlParamsHelper(clearPreloadData)
    }, [clearPreloadData])

    const fetchPreloadData = useCallback(
        async (codPrograma: string, modalidad: string) => {
            return fetchPreloadDataHelper(
                codPrograma,
                modalidad,
                resourcesApi,
                setIsLoading,
                setError,
                setPreloadData,
                onDataLoaded
            )
        },
        [resourcesApi, onDataLoaded]
    )

    const processPreload = useCallback(async () => {
        const codPrograma = sessionStorage.getItem('preload_codPrograma')
        const modalidad = sessionStorage.getItem('preload_modalidad')

        if (
            codPrograma &&
            modalidad &&
            isAuthenticated &&
            !hasProcessedPreload
        ) {
            setHasProcessedPreload(true)
            await fetchPreloadData(codPrograma, modalidad)
        }
    }, [isAuthenticated, fetchPreloadData, hasProcessedPreload])

    useEffect(() => {
        captureUrlParams()
    }, [captureUrlParams])

    useEffect(() => {
        if (isAuthenticated && !hasProcessedPreload) {
            const codPrograma = sessionStorage.getItem('preload_codPrograma')
            const modalidad = sessionStorage.getItem('preload_modalidad')

            if (codPrograma && modalidad) {
                processPreload()
            }
        } else if (!isAuthenticated && hasProcessedPreload) {
            clearPreloadData()
        }
    }, [isAuthenticated, hasProcessedPreload, processPreload, clearPreloadData])

    const hasPreloadData = Boolean(
        sessionStorage.getItem('preload_codPrograma') &&
            sessionStorage.getItem('preload_modalidad')
    )

    return {
        preloadData,
        isLoading,
        error,
        clearPreloadData,
        hasPreloadData
    }
}
